// Hoisted to module level to avoid creating a new instance on every call
const rtf = new Intl.RelativeTimeFormat("en", { numeric: "always" });

/**
 * Formats an ISO 8601 date string as a human-readable relative time (e.g. "6 hours ago", "2 weeks ago").
 * Automatically selects the most appropriate unit (minutes, hours, days, weeks, months, or years).
 *
 * @param dateString - An ISO 8601 timestamp string, or undefined.
 * @returns A relative time string, "Just now" if the difference is less than a minute, or "Never" if the input is undefined.
 */
export const formatRelativeTime = (dateString?: string): string => {
  if (!dateString) return "Never";
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.round((date.getTime() - now.getTime()) / 1000);

  const absDiff = Math.abs(diffInSeconds);
  if (absDiff < 60) return "Just now";
  if (absDiff < 3600)
    return rtf.format(Math.trunc(diffInSeconds / 60), "minute");
  if (absDiff < 86400)
    return rtf.format(Math.trunc(diffInSeconds / 3600), "hour");
  if (absDiff < 604800)
    return rtf.format(Math.trunc(diffInSeconds / 86400), "day");
  if (absDiff < 2592000)
    return rtf.format(Math.trunc(diffInSeconds / 604800), "week");
  if (absDiff < 31536000)
    return rtf.format(Math.trunc(diffInSeconds / 2592000), "month");
  return rtf.format(Math.trunc(diffInSeconds / 31536000), "year");
};

/**
 * Formats an ISO 8601 date string as a localized date-time string without seconds.
 *
 * @param dateString - An ISO 8601 timestamp string, or undefined.
 * @returns A formatted date-time string (e.g. "1/15/2025, 3:45 PM EST"), or "unknown time" if the input is undefined or invalid.
 */
export const shortLocalDateTime = (dateString?: string): string => {
  if (!dateString) return "unknown time";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "unknown time";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    timeZoneName: "short",
  });
};

/**
 * Determines if an ISO 8601 date string is within a given number of days from today, in either direction.
 *
 * @param dateString - An ISO 8601 timestamp string
 * @param days - The number of days to check against
 * @returns true if the date is within the specified number of days of today, otherwise false
 */
export const isWithinXDays = (dateString: string, days: number): boolean => {
  if (days <= 0) return false;
  const diffInDays = Math.abs(
    (new Date().getTime() - new Date(dateString).getTime()) /
      (1000 * 60 * 60 * 24),
  );
  return diffInDays <= days;
};
